/**
 * Images, videos and audio inside a tool's result, whatever shape the provider gave it:
 * - Claude: content blocks `{type:"image", source:{type:"base64", media_type, data}}`
 * - MCP as the server sent it: `{type:"image"|"audio", data, mimeType}`, embedded resources
 *   `{type:"resource", resource:{blob|uri, mimeType}}` and `{type:"resource_link", uri, mimeType}`
 * - Codex and Antigravity: the MCP result as a JSON string (`{"content":[…]}`), sometimes with another tool's JSON
 *   result nested inside a text part
 */
export interface ToolMedia {
  kind: "image" | "video" | "audio";
  src: string;
  name: string;
  /** a link to another site: loaded only when asked (an address can carry data out) */
  remote: boolean;
}

const MAX_DEPTH = 4;

function kindOf(mime: string | undefined, uri?: string): ToolMedia["kind"] | null {
  const m = (mime ?? "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  if (!m && uri) return mediaKindOfUrl(uri);
  return null;
}

/** A link's media kind by its extension (for links in answers and resource links without a type). */
export function mediaKindOfUrl(url: string): ToolMedia["kind"] | null {
  const path = url.split(/[?#]/)[0]!.toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogv)$/.test(path)) return "video";
  if (/\.(mp3|m4a|wav|ogg|oga|aac|flac)$/.test(path)) return "audio";
  if (/\.(png|jpe?g|gif|webp|avif|svg)$/.test(path)) return "image";
  return null;
}

function parseJsonish(s: string): unknown {
  const t = s.trimStart();
  if (!t.startsWith("{") && !t.startsWith("[")) return undefined;
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

const ext = (mime: string) => (mime.split("/")[1] ?? "bin").split(/[;+]/)[0]!.replace("jpeg", "jpg").replace("quicktime", "mov");
const fileName = (uri: string | undefined, kind: string, mime: string, n: number) => {
  const last = uri?.split(/[?#]/)[0]?.split("/").pop();
  return last && /\.[a-z0-9]{2,5}$/i.test(last) ? last : `${kind}-${n}.${ext(mime || `${kind}/bin`)}`;
};

export function mediaOf(content: unknown): ToolMedia[] {
  const out: ToolMedia[] = [];
  const seen = new Set<string>();
  const push = (m: Omit<ToolMedia, "name"> & { uri?: string | undefined; mime: string }) => {
    if (seen.has(m.src)) return;
    seen.add(m.src);
    out.push({ kind: m.kind, src: m.src, remote: m.remote, name: fileName(m.uri, m.kind, m.mime, out.length + 1) });
  };
  const walk = (v: unknown, depth: number): void => {
    if (depth > MAX_DEPTH || v === null || v === undefined) return;
    if (typeof v === "string") {
      const parsed = parseJsonish(v);
      if (parsed !== undefined) walk(parsed, depth + 1);
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) walk(item, depth);
      return;
    }
    if (typeof v !== "object") return;
    const b = v as Record<string, unknown>;
    const type = b.type;
    const source = b.source as { type?: string; media_type?: string; data?: string } | undefined;
    if (type === "image" && source?.type === "base64" && typeof source.data === "string") {
      const mime = source.media_type ?? "image/png";
      return push({ kind: "image", src: `data:${mime};base64,${source.data}`, remote: false, mime });
    }
    if ((type === "image" || type === "audio") && typeof b.data === "string") {
      const mime = typeof b.mimeType === "string" ? b.mimeType : `${type}/png`;
      const kind = kindOf(mime) ?? type;
      return push({ kind, src: `data:${mime};base64,${b.data}`, remote: false, mime });
    }
    if (type === "resource" && b.resource && typeof b.resource === "object") {
      const r = b.resource as { blob?: unknown; uri?: unknown; mimeType?: unknown; text?: unknown };
      const mime = typeof r.mimeType === "string" ? r.mimeType : "";
      const uri = typeof r.uri === "string" ? r.uri : undefined;
      const kind = kindOf(mime, uri);
      if (kind && typeof r.blob === "string") return push({ kind, src: `data:${mime || `${kind}/bin`};base64,${r.blob}`, remote: false, uri, mime });
      if (kind && uri && /^https?:\/\//i.test(uri)) return push({ kind, src: uri, remote: true, uri, mime });
      if (typeof r.text === "string") walk(r.text, depth + 1);
      return;
    }
    if (type === "resource_link" && typeof b.uri === "string" && /^https?:\/\//i.test(b.uri)) {
      const mime = typeof b.mimeType === "string" ? b.mimeType : "";
      const kind = kindOf(mime, b.uri);
      if (kind) push({ kind, src: b.uri, remote: true, uri: b.uri, mime });
      return;
    }
    if (type === "text" && typeof b.text === "string") return walk(b.text, depth + 1);
    // an MCP result object ({content:[…], structured_content}) or a provider's wrapper around one
    if ("content" in b) walk(b.content, depth);
  };
  walk(content, 0);
  return out;
}

/** The readable part of a tool's result: text parts, without the JSON wrapping and base64 bodies around them. */
export function textOf(content: unknown): string {
  if (typeof content === "string") {
    const parsed = parseJsonish(content);
    const inner = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as { content?: unknown }).content : undefined;
    return Array.isArray(inner) ? textOf(inner) : content;
  }
  if (Array.isArray(content)) return (content as { type: string; text?: string }[]).filter((b) => b?.type === "text").map((b) => b.text ?? "").join("\n");
  return JSON.stringify(content, null, 2);
}

/** The web links in a tool's result, its JSON wrappings opened (e.g. Medad's export: {"url": "https://…/dl/…"}). */
export function linksOf(content: unknown): string[] {
  const out = new Set<string>();
  const walk = (v: unknown, depth: number): void => {
    if (depth > MAX_DEPTH || v === null || v === undefined) return;
    if (typeof v === "string") {
      const parsed = parseJsonish(v);
      if (parsed !== undefined) return walk(parsed, depth + 1);
      for (const m of v.matchAll(/https?:\/\/[^\s"'<>()\\[\]{}]+/g)) out.add(m[0].replace(/[.,;:!?]+$/, ""));
      return;
    }
    if (Array.isArray(v)) return v.forEach((x) => walk(x, depth));
    if (typeof v === "object") for (const x of Object.values(v as Record<string, unknown>)) walk(x, depth);
  };
  walk(content, 0);
  return [...out];
}
